import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';

// Update the LoadingStrategy type to include "progressive"
type LoadingStrategy = 'full' | 'minimal' | 'progressive';

interface LoadingStrategyContextType {
  strategy: LoadingStrategy;
  setStrategy: (strategy: LoadingStrategy) => void;
  isSlowConnection: boolean;
  isHighRtt: boolean;
  markFontLoaded: () => void;
}

const LoadingStrategyContext = createContext<LoadingStrategyContextType>({
  strategy: 'full',
  setStrategy: () => {},
  isSlowConnection: false,
  isHighRtt: false,
  markFontLoaded: () => {}
});

export const LoadingStrategyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [strategy, setStrategy] = useState<LoadingStrategy>('full');
  const [fontLoaded, setFontLoaded] = useState(false);
  const networkStatus = useNetworkStatus();
  
  const isSlowConnection = 
    networkStatus.effectiveType === 'slow-2g' || 
    networkStatus.effectiveType === '2g' || 
    networkStatus.effectiveType === '3g';
    
  // Consider high RTT as anything over 500ms  
  const isHighRtt = networkStatus.rtt !== undefined && networkStatus.rtt > 500;
  
  const markFontLoaded = () => {
    setFontLoaded(true);
    console.log('Fonts marked as loaded');
  };
  
  useEffect(() => {
    // Adjust strategy based on connection quality
    if (networkStatus.effectiveType === 'slow-2g' || 
       (networkStatus.effectiveType === '2g' && networkStatus.saveData)) {
      console.info('Applying minimal loading strategy (very slow connection)');
      setStrategy('minimal');
    } else if (networkStatus.effectiveType === '3g' || 
              (networkStatus.rtt && networkStatus.rtt > 300)) {
      console.info(`Applying progressive loading strategy (medium connection)`);
      setStrategy('progressive');
    } else {
      console.info(`Applying full loading strategy (${networkStatus.effectiveType}, RTT: ${networkStatus.rtt}ms)`);
      setStrategy('full');
    }
  }, [networkStatus]);
  
  return (
    <LoadingStrategyContext.Provider 
      value={{ 
        strategy, 
        setStrategy,
        isSlowConnection,
        isHighRtt,
        markFontLoaded
      }}
    >
      {children}
    </LoadingStrategyContext.Provider>
  );
};

export const useLoadingStrategy = () => useContext(LoadingStrategyContext);